import { useEffect, useState } from "react";
import { fetchDay } from "@/lib/api";
import type { DayData } from "@/types/lex";

export type DayState =
  | { status: "loading"; data: null; error: null }
  | { status: "ready"; data: DayData; error: null }
  | { status: "error"; data: null; error: Error };

/**
 * Fetches one day's JSON. Re-fetches whenever `day` changes. Surfaces a
 * tri-state so the caller can render skeleton, content, or an error UI.
 *
 * The underlying fetch is de-duped at the module level (see `api.ts`), so
 * mounting multiple components for the same day costs one network request.
 */
export function useDayData(day: number): DayState {
  const [state, setState] = useState<DayState>({ status: "loading", data: null, error: null });

  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading", data: null, error: null });
    fetchDay(day)
      .then((data) => {
        if (cancelled) return;
        setState({ status: "ready", data, error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const error = err instanceof Error ? err : new Error(String(err));
        setState({ status: "error", data: null, error });
      });
    return () => {
      cancelled = true;
    };
  }, [day]);

  return state;
}
